import { useState, useEffect } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';

interface EndpointFormData {
  id?: number;
  name: string;
  description: string;
  site: string;
  kind: 'infra' | 'customer' | 'discovered_unmanaged' | 'other';
  ip_address: string;
  probe_method: 'ping' | 'tcp' | 'ping_then_tcp' | 'https' | 'dns_query' | 'sip_options' | 'http_check' | 'smtp_banner';
  tcp_port: number | null;
  poll_interval_sec: number;
  customer_id: number | null;
  enabled: boolean;
  silent: boolean;
  metadata: {
    location?: 'internet' | 'external' | 'internal';
    type?: string;
  };
}

const EMPTY_FORM: EndpointFormData = {
  name: '', description: '', site: '',
  kind: 'infra', ip_address: '',
  probe_method: 'ping_then_tcp', tcp_port: null,
  poll_interval_sec: 300,
  customer_id: null, enabled: true, silent: false,
  metadata: { location: 'internal', type: '' },
};

export function EndpointEditModal({
  open, onClose, editing,
}: {
  open: boolean;
  onClose: () => void;
  editing: Partial<EndpointFormData> | null;
}) {
  const [form, setForm] = useState<EndpointFormData>(EMPTY_FORM);
  const qc = useQueryClient();
  const toast = useToast();

  useEffect(() => {
    if (open) {
      setForm({
        ...EMPTY_FORM,
        ...(editing ?? {}),
        metadata: { ...EMPTY_FORM.metadata, ...((editing?.metadata as Record<string, unknown>) ?? {}) },
      } as EndpointFormData);
    }
  }, [open, editing]);

  const { data: customers } = useQuery({
    queryKey: ['customers-light'],
    queryFn: () => api.get<{ data: Array<{ id: number; name: string; external_id: string | null }> }>('/customers', { params: { per_page: 500 } }).then((r) => r.data.data ?? r.data),
    enabled: open,
  });

  const isEdit = !!editing?.id;

  // Validation client-side : retourne {ok, error}
  const validate = (): { ok: boolean; error: string | null } => {
    if (!form.name.trim()) return { ok: false, error: 'Nom requis' };
    if (!form.ip_address.trim()) return { ok: false, error: 'IP / hostname requis' };
    const ip = form.ip_address.trim();
    const isIpv4 = /^\d{1,3}(\.\d{1,3}){3}$/.test(ip);
    const isHost = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*$/.test(ip);
    const isIpv6 = /^[0-9a-fA-F:]+$/.test(ip) && ip.includes(':');
    if (!isIpv4 && !isHost && !isIpv6) return { ok: false, error: 'IP/hostname invalide (ex: 8.8.8.8 ou google.com)' };
    if (isIpv4) {
      const parts = ip.split('.').map(Number);
      if (parts.some((p) => p < 0 || p > 255)) return { ok: false, error: 'IPv4 hors limites (0-255)' };
    }
    if ((form.probe_method === 'tcp' || form.probe_method === 'sip_options' || form.probe_method === 'smtp_banner') && !form.tcp_port) {
      return { ok: false, error: `Port TCP requis pour méthode ${form.probe_method}` };
    }
    if (form.tcp_port !== null && (form.tcp_port < 1 || form.tcp_port > 65535)) {
      return { ok: false, error: 'Port TCP doit être entre 1 et 65535' };
    }
    if (form.poll_interval_sec < 30) return { ok: false, error: 'Poll interval ≥ 30s (sinon spam)' };
    return { ok: true, error: null };
  };
  const validation = validate();

  const saveMut = useMutation({
    mutationFn: () => {
      const payload = {
        name: form.name, description: form.description || null, site: form.site || null,
        kind: form.kind, ip_address: form.ip_address,
        probe_method: form.probe_method, tcp_port: form.tcp_port,
        poll_interval_sec: form.poll_interval_sec,
        customer_id: form.customer_id, enabled: form.enabled, silent: form.silent,
        metadata: form.metadata,
      };
      return isEdit
        ? api.patch(`/endpoints/${editing!.id}`, payload).then((r) => r.data)
        : api.post('/endpoints', payload).then((r) => r.data);
    },
    onSuccess: () => {
      toast.push({ kind: 'success', title: isEdit ? 'Endpoint modifié' : 'Endpoint créé' });
      qc.invalidateQueries({ queryKey: ['endpoints'] });
      qc.invalidateQueries({ queryKey: ['endpoint', String(editing?.id)] });
      onClose();
    },
    onError: (e: any) => toast.push({ kind: 'error', title: 'Erreur · ' + (e?.response?.data?.message ?? e.message ?? 'inconnue') }),
  });

  if (!open) return null;
  const customerList = Array.isArray(customers) ? customers : (customers as any)?.data ?? [];

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? `Modifier · ${form.name}` : 'Nouvel endpoint'}>
      <div className="grid grid-cols-2 gap-3 text-xs">
        <FormField label="Nom" required>
          <input className="input w-full" value={form.name}
                 onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </FormField>
        <FormField label="Site (ex: yul1, aws-ca-east, internet)">
          <input className="input w-full" value={form.site}
                 onChange={(e) => setForm({ ...form, site: e.target.value })} placeholder="libre" />
        </FormField>

        <FormField label="Catégorie macro" required>
          <select className="input w-full" value={form.kind}
                  onChange={(e) => setForm({ ...form, kind: e.target.value as EndpointFormData['kind'] })}>
            <option value="infra">infra (notre infra ou cible publique)</option>
            <option value="customer">customer (équipement client)</option>
            <option value="discovered_unmanaged">discovered_unmanaged</option>
            <option value="other">other</option>
          </select>
        </FormField>
        <FormField label="Location">
          <select className="input w-full" value={form.metadata.location ?? 'internal'}
                  onChange={(e) => setForm({ ...form, metadata: { ...form.metadata, location: e.target.value as 'internet' | 'external' | 'internal' } })}>
            <option value="internal">internal (chez Filos)</option>
            <option value="external">external (cloud / autre DC)</option>
            <option value="internet">internet (cible publique)</option>
          </select>
        </FormField>

        <FormField label="Type">
          <input className="input w-full" list="endpoint-types" value={form.metadata.type ?? ''}
                 onChange={(e) => setForm({ ...form, metadata: { ...form.metadata, type: e.target.value } })}
                 placeholder="dns, voip_sbc, olt, encoder..." />
          <datalist id="endpoint-types">
            {/* Réseau / infra core */}
            <option value="olt" />
            <option value="router_core" />
            <option value="router_edge" />
            <option value="switch_distribution" />
            <option value="switch_access" />
            <option value="firewall" />
            <option value="load_balancer" />
            {/* Voix */}
            <option value="voip_sbc" />
            <option value="voip_pbx" />
            <option value="voip_registrar" />
            <option value="voip_media_server" />
            <option value="voicemail" />
            {/* Vidéo / IPTV */}
            <option value="iptv_backend" />
            <option value="iptv_middleware" />
            <option value="video_encoder" />
            <option value="video_transcoder" />
            <option value="pvr" />
            <option value="cdn_cache" />
            {/* Réseaux services */}
            <option value="dns" />
            <option value="dhcp_server" />
            <option value="radius" />
            <option value="ntp_server" />
            {/* Web / API / mail */}
            <option value="web_server" />
            <option value="api_service" />
            <option value="mail_smtp" />
            <option value="mail_imap" />
            <option value="mail_webmail" />
            {/* Internet (cibles publiques) */}
            <option value="cdn" />
            <option value="streaming" />
            <option value="gaming" />
            <option value="social" />
            <option value="cloud" />
            <option value="workspace" />
            {/* Client / autre */}
            <option value="cpe" />
            <option value="ap_wifi" />
            <option value="camera" />
            <option value="printer" />
            <option value="server" />
            <option value="other" />
          </datalist>
        </FormField>
        <FormField label="Customer (optionnel)">
          <select className="input w-full" value={form.customer_id ?? ''}
                  onChange={(e) => setForm({ ...form, customer_id: e.target.value ? Number(e.target.value) : null })}>
            <option value="">— aucun —</option>
            {customerList.map((c: { id: number; name: string; external_id: string | null }) => (
              <option key={c.id} value={c.id}>{c.name}{c.external_id ? ` (${c.external_id})` : ''}</option>
            ))}
          </select>
        </FormField>

        <FormField label="IP / hostname" required>
          <input className="input w-full font-mono" value={form.ip_address}
                 onChange={(e) => setForm({ ...form, ip_address: e.target.value })}
                 placeholder="8.8.8.8 ou example.com" />
        </FormField>
        <FormField label="Méthode probe">
          <select className="input w-full" value={form.probe_method}
                  onChange={(e) => setForm({ ...form, probe_method: e.target.value as EndpointFormData['probe_method'] })}>
            <option value="ping">ping (ICMP)</option>
            <option value="tcp">tcp (port spécifique)</option>
            <option value="ping_then_tcp">ping_then_tcp (fallback)</option>
            <option value="https">https (HEAD request)</option>
          </select>
        </FormField>

        <FormField label="Port TCP (si méthode TCP)">
          <input type="number" className="input w-full font-mono" value={form.tcp_port ?? ''}
                 onChange={(e) => setForm({ ...form, tcp_port: e.target.value ? Number(e.target.value) : null })}
                 placeholder="443" min={1} max={65535} />
        </FormField>
        <FormField label="Poll interval (sec)">
          <input type="number" className="input w-full font-mono" value={form.poll_interval_sec}
                 onChange={(e) => setForm({ ...form, poll_interval_sec: Number(e.target.value) })}
                 min={30} max={86400} />
        </FormField>

        <FormField label="Activé">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
            <span className="text-text-muted">Le cron probe ce endpoint</span>
          </label>
        </FormField>
        <FormField label="Silent (pas d'alerte)">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={form.silent} onChange={(e) => setForm({ ...form, silent: e.target.checked })} />
            <span className="text-text-muted">Pas d'event sur transitions (utile auto-discovered)</span>
          </label>
        </FormField>

        <div className="col-span-2">
          <FormField label="Description">
            <textarea className="input w-full" rows={2} value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </FormField>
        </div>
      </div>

      {validation.error && (
        <div className="mt-3 bg-danger/10 border border-danger/30 text-danger text-xs rounded p-2">
          ⚠ {validation.error}
        </div>
      )}

      <div className="mt-4 flex items-center justify-end gap-2">
        <button className="btn" onClick={onClose}>Annuler</button>
        <button className="btn btn-primary"
                disabled={!validation.ok || saveMut.isPending}
                onClick={() => saveMut.mutate()}>
          {saveMut.isPending ? '…' : isEdit ? 'Enregistrer' : 'Créer'}
        </button>
      </div>
    </Modal>
  );
}

function FormField({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[10px] uppercase tracking-wider text-text-faint font-medium">
        {label}{required && <span className="text-danger ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}
